'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Settings, Key, Bell, GitBranch, Save, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
}

function Toggle({ checked, onChange }: ToggleProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5.5 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted-foreground/30'}`}
      style={{ height: '22px' }}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : ''}`}
      />
    </button>
  );
}

function SectionHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-border mb-5">
      <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Icon size={15} className="text-primary" />
      </div>
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [saved, setSaved] = useState(false);
  const [showTokens, setShowTokens] = useState<Record<string, boolean>>({});

  // Pipeline settings
  const [pipelineInterval, setPipelineInterval] = useState('15');
  const [maxReelsPerDay, setMaxReelsPerDay] = useState('8');
  const [minVelocityScore, setMinVelocityScore] = useState('60');
  const [autoPilot, setAutoPilot] = useState(true);
  const [autoPublish, setAutoPublish] = useState(true);
  const [dmAutoReply, setDmAutoReply] = useState(true);

  // Notification settings
  const [notifyPublish, setNotifyPublish] = useState(true);
  const [notifyError, setNotifyError] = useState(true);
  const [notifyTrend, setNotifyTrend] = useState(true);
  const [notifyDm, setNotifyDm] = useState(false);
  const [notifyQueue, setNotifyQueue] = useState(true);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggleShow = (key: string) => setShowTokens((prev) => ({ ...prev, [key]: !prev[key] }));
  const mask = (val: string) => val.slice(0, 6) + '••••••••••••••••••••';

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Settings size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Settings</h1>
              <p className="text-xs text-muted-foreground font-mono">Pipeline configuration &amp; preferences</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              saved
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' :'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
            {saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Pipeline Config */}
          <div className="rounded-xl border border-border bg-card p-5">
            <SectionHeader icon={GitBranch} title="Pipeline Configuration" description="Automation and publishing behaviour" />
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground font-medium">Auto-Pilot Mode</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Fully automated trend → publish cycle</p>
                </div>
                <Toggle checked={autoPilot} onChange={setAutoPilot} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground font-medium">Auto-Publish Reels</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Publish directly without manual review</p>
                </div>
                <Toggle checked={autoPublish} onChange={setAutoPublish} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground font-medium">DM Auto-Reply</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Trigger DM funnel on keyword match</p>
                </div>
                <Toggle checked={dmAutoReply} onChange={setDmAutoReply} />
              </div>
              <div className="pt-2 border-t border-border space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Pipeline Interval (minutes)
                  </label>
                  <input
                    type="number"
                    value={pipelineInterval}
                    onChange={(e) => setPipelineInterval(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Max Reels Per Day
                  </label>
                  <input
                    type="number"
                    value={maxReelsPerDay}
                    onChange={(e) => setMaxReelsPerDay(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Min Signal Velocity Score (0–100)
                  </label>
                  <input
                    type="number"
                    value={minVelocityScore}
                    onChange={(e) => setMinVelocityScore(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Notifications */}
            <div className="rounded-xl border border-border bg-card p-5">
              <SectionHeader icon={Bell} title="Notification Preferences" description="Choose what triggers an alert" />
              <div className="space-y-4">
                {[
                  { label: 'Reel published', desc: 'Alert when a reel goes live', val: notifyPublish, set: setNotifyPublish },
                  { label: 'Pipeline errors', desc: 'Alert on failures and timeouts', val: notifyError, set: setNotifyError },
                  { label: 'High-velocity trends', desc: 'Alert when velocity ≥ 80', val: notifyTrend, set: setNotifyTrend },
                  { label: 'DM funnel triggers', desc: 'Alert on keyword-matched DMs', val: notifyDm, set: setNotifyDm },
                  { label: 'Queue capacity warnings', desc: 'Alert when queue > 70%', val: notifyQueue, set: setNotifyQueue },
                ].map(({ label, desc, val, set }) => (
                  <div key={label} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-foreground font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                    <Toggle checked={val} onChange={set} />
                  </div>
                ))}
              </div>
            </div>

            {/* API Credentials */}
            <div className="rounded-xl border border-border bg-card p-5">
              <SectionHeader icon={Key} title="API Credentials" description="Connected service tokens (read-only)" />
              <div className="space-y-3">
                {[
                  { label: 'OpenAI API Key', value: 'sk-proj-••••••••••••••••••••••••••••••••', key: 'openai' },
                  { label: 'Meta Access Token', value: 'EAABwzLiBAAHBO••••••••••••••••••••••••', key: 'meta' },
                  { label: 'FB Page Access Token', value: 'EAABwzLiBAAHBO••••••••••••••••••••••••', key: 'fb' },
                ].map(({ label, value, key }) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1">
                      {label}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        type={showTokens[key] ? 'text' : 'password'}
                        value={value}
                        className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border text-xs text-muted-foreground font-mono focus:outline-none"
                      />
                      <button
                        onClick={() => toggleShow(key)}
                        className="p-2 rounded-lg bg-secondary border border-border text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showTokens[key] ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">
                  To update credentials, edit your <code className="font-mono bg-secondary px-1 rounded">.env</code> file directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
